import React, {Fragment, PureComponent} from 'react';
import PropTypes from 'prop-types';
import {Link} from 'react-router-dom';

import {Link as LocalizedLink} from '@homeaway/react-localization-components';

import translate from '../../../utils/translations';
import {propertyIdSelected} from '../../../utils/edapUtils';
import {
    getAdultsLocalizationKey,
    getChildrenLocalizationKey,
} from '../../../utils/guests';
import {Countdown} from '../Countdown/Countdown';
import PaymentLink from '../../molecules/PaymentLink/PaymentLink';
import StayContext from '../../context/StayContext';
import {VrbexConsumer} from '../../context/VrbexContext';
import {withErrorBoundary} from '../../ErrorBoundary/ErrorBoundary';

import {FallbackUI} from '../../molecules/FallbackUI/FallbackUI';

import './TripSummary.less';

class TripSummary extends PureComponent {
    static propTypes = {
        actionLocation: PropTypes.string,
        backUrl: PropTypes.string,
        isHybridInbox: PropTypes.bool,
        translations: PropTypes.shape({}),
        expirationDateTime: PropTypes.string,
    };

    static defaultProps = {
        backUrl: '/traveler/th/inbox',
        translations: {},
    };

    static contextType = StayContext;

    get stayContext() {
        if (!this.context) {
            throw new Error(
                'stayContext must be used within a StayContextProvider'
            );
        }
        return this.context;
    }

    statusLabel(reservationStatus, statusCode, statusDescription) {
        if (!statusDescription) {
            statusDescription = reservationStatus; // reservationstatus should be shown in case of inquiry
        }
        switch (statusCode) {
            case 'RESERVE':
                return (
                    <span className="label label-success">
                        {statusDescription}
                    </span>
                );
            case 'CANCEL':
                return (
                    <span className="label label--black">
                        {statusDescription}
                    </span>
                );
            default:
                return (
                    <span className="label label-default">
                        {statusDescription}
                    </span>
                );
        }
    }

    propertyIdClicked = () => {
        const {actionLocation} = this.props;
        const {
            stay: {
                booking: {reservationUuid},
                unit: {listingId},
            },
        } = this.stayContext;

        propertyIdSelected({actionLocation, listingId, reservationUuid});
    };

    getGuests = (numberOfAdults, numberOfChildren) => {
        const {translations} = this.props;
        const guests = [
            translate(
                translations,
                'trav-dash/trav-home',
                'messages',
                getAdultsLocalizationKey(`${numberOfAdults}`),
                numberOfAdults
            ),
        ];
        if (numberOfChildren) {
            guests.push(
                translate(
                    translations,
                    'trav-dash/trav-home',
                    'messages',
                    getChildrenLocalizationKey(`${numberOfChildren}`),
                    numberOfChildren
                )
            );
        }

        return guests.join(', ');
    };

    render() {
        const {
            backUrl,
            expirationDateTime,
            isHybridInbox,
            isVrbexNative,
            translations,
        } = this.props;
        const {
            stay: {
                booking: {
                    checkin: {date: checkinDate, time: checkInTime},
                    checkout: {date: checkoutDate, time: checkOutTime},
                    guests: {numberOfAdults, numberOfChildren},
                    reservationStatus,
                    status: {code: statusCode, description: statusDescription},
                    totalPrice: {paymentDetails},
                },
                ctaDetails: {tripPaymentsLink},
                conversation: {id: conversationId},
                unit: {id, listingId, url: unitUrl},
            },
        } = this.stayContext;

        const ViewDetailsLabel = translate(
            translations,
            'trav-dash/trav-home',
            'messages',
            'th.inbox.conversation.buttons.quote'
        );

        return (
            <div className="TripSummary rounded-border">
                {checkinDate && (
                    <Fragment>
                        <Countdown
                            expirationDateTime={expirationDateTime}
                            translations={translations}
                        />
                        <div className="trip-dates-section trip-dates-section__hideTripDatesSection">
                            <div className="trip-dates-section__arrival">
                                <div className="trip-dates-section__date">
                                    <div>
                                        {translate(
                                            translations,
                                            'trav-dash/trav-home',
                                            'messages',
                                            'th.bookings.tripSummary.arrive'
                                        )}
                                    </div>
                                    <h3>{checkinDate}</h3>
                                </div>
                                {checkInTime && (
                                    <div className="trip-dates-section__time">
                                        <div>
                                            {translate(
                                                translations,
                                                'trav-dash/trav-home',
                                                'messages',
                                                'th.bookings.tripSummary.checkin'
                                            )}
                                        </div>
                                        <h3>{checkInTime}</h3>
                                    </div>
                                )}
                            </div>

                            <div className="trip-dates-section__departure">
                                <div className="trip-dates-section__date">
                                    <div>
                                        {translate(
                                            translations,
                                            'trav-dash/trav-home',
                                            'messages',
                                            'th.bookings.tripSummary.depart'
                                        )}
                                    </div>
                                    <h3>{checkoutDate}</h3>
                                </div>
                                {checkOutTime && (
                                    <div className="trip-dates-section__time">
                                        <div>
                                            {translate(
                                                translations,
                                                'trav-dash/trav-home',
                                                'messages',
                                                'th.bookings.tripSummary.checkout'
                                            )}
                                        </div>
                                        <h3>{checkOutTime}</h3>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Fragment>
                )}

                <div className="booking-section">
                    {reservationStatus && (
                        <div className="booking-section__line">
                            <span>
                                {translate(
                                    translations,
                                    'trav-dash/trav-home',
                                    'messages',
                                    'th.bookings.tripSummary.status'
                                )}
                            </span>
                            {this.statusLabel(
                                reservationStatus,
                                statusCode,
                                statusDescription
                            )}
                        </div>
                    )}

                    {listingId && unitUrl && (
                        <div
                            className="booking-section__line"
                            onClick={this.propertyIdClicked}
                        >
                            <span>
                                {translate(
                                    translations,
                                    'trav-dash/trav-home',
                                    'messages',
                                    'th.inbox.conversation.propertySummary.property.id'
                                )}
                            </span>
                            <LocalizedLink href={unitUrl} target="_blank">
                                {listingId}
                            </LocalizedLink>
                        </div>
                    )}

                    {id && (
                        <div className="booking-section__line">
                            <span>
                                {translate(
                                    translations,
                                    'trav-dash/trav-home',
                                    'messages',
                                    'th.inbox.conversation.propertySummary.reservation.id'
                                )}
                            </span>
                            <span>{id}</span>
                        </div>
                    )}

                    {numberOfAdults && (
                        <div className="booking-section__line">
                            <span>
                                {translate(
                                    translations,
                                    'trav-dash/trav-home',
                                    'messages',
                                    'th.inbox.conversation.propertySummary.guests'
                                )}
                            </span>
                            <span>
                                {this.getGuests(
                                    numberOfAdults,
                                    numberOfChildren
                                )}
                            </span>
                        </div>
                    )}

                    {paymentDetails && paymentDetails[0] && (
                        <Fragment>
                            <div className="booking-section__line">
                                <strong>{paymentDetails[0].title}</strong>
                                <strong>{paymentDetails[0].amount}</strong>
                            </div>
                            <div className="booking-section__line booking-section__line--subvalue">
                                {isHybridInbox ? (
                                    <PaymentLink
                                        backUrl={backUrl}
                                        conversationId={conversationId}
                                        isVrbexNative={isVrbexNative}
                                        tripPaymentsLink={tripPaymentsLink}
                                    >
                                        {ViewDetailsLabel}
                                    </PaymentLink>
                                ) : (
                                    <Link
                                        to={{
                                            pathname: `/traveler/th/inbox/conversation/${conversationId}/payments`,
                                            state: {
                                                backUrl,
                                                tabChange: true,
                                            },
                                        }}
                                        data-testid="link_rendered"
                                    >
                                        {ViewDetailsLabel}
                                    </Link>
                                )}
                            </div>
                        </Fragment>
                    )}
                </div>
            </div>
        );
    }
}

const TripSummaryWithErrorBoundary = (props) => {
    const {translations} = props;
    const ERROR_BOUNDARY_TEXT = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'error.boundary.message'
    );
    const ERROR_BOUNDARY_TITLE = translate(
        translations,
        'trav-dash/trav-home',
        'messages',
        'th.confirmation.rentalInfo.title'
    );

    return withErrorBoundary(
        <VrbexConsumer>
            {({isVrbexNative}) => (
                <TripSummary {...props} isVrbexNative={isVrbexNative} />
            )}
        </VrbexConsumer>,
        'trip-summary',
        <FallbackUI
            errorText={ERROR_BOUNDARY_TEXT}
            title={ERROR_BOUNDARY_TITLE}
        />
    );
};
export {TripSummaryWithErrorBoundary as TripSummary};
